import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { IRequestCallback, IRequestCallbackFunction, RequestService } from '@df/api/request/request.service';
import { RecentlyViewedService } from '@df/catalog-ui/recently-viewed/recently-viewed.service';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Url } from '@df/core/service/url';
import { STATUS_ERROR, Status } from '@df/core/status';
import { WINDOW } from '@df/dom/globals';
import { KlevuTrackService } from '@df/module-klevu-search/klevu-track.service';
import { TimeoutService } from '@df/ng/timeout.service';
import { SearchResult } from '@df/search/search-result';
import { SearchResultFactoryService } from '@df/search/search-result-factory.service';
import { Search } from '@df/search/search.interfaces';
import { SearchService } from '@df/search/search.service';
import { ukDate } from '@df/util/fn/uk-date';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import flatten from 'lodash-es/flatten';
import mapValues from 'lodash-es/mapValues';
import { KlevuSearch } from './klevu-interfaces';
import { NDfModuleKlevuSearch } from './klevu-search.namespace';

declare module '@df/search/search.interfaces' {
    namespace Search {
        interface IResult {
            direct?: KlevuSearch.ISearchResponse;
        }

        interface ITerm {
            barePhrase: string;
        }
    }
}

const TERMS_SEARCH_ID = 'terms';
export const PRODUCTS_SEARCH_ID = 'product';
const CATEGORIES_SEARCH_ID = 'categories';
const CATNAV_SEARCH_ID = 'categoryProducts';
const CMS_SEARCH_ID = 'cms';

@Injectable({
    providedIn: 'root'
})
export class KlevuSearchService extends SearchService {
    /**
     * List of popular terms
     * @see loadKlevuVars
     */
    webstorePopularTerms?: string[];

    noResultMessage?: string;

    banners?: KlevuSearch.IBanner[];

    /**
     * List of popular products
     * @see loadKlevuVars
     */
    popularProductsOfSite?: Product<IProductDataV2>[];

    private _redirects?: KlevuSearch.IKeywordUrlMap[];

    private trendingProductsPromise?: Promise<SearchResult>;

    private window = inject(WINDOW);

    constructor(
        protected url: Url,
        protected app: App,
        protected http: HttpClient,
        private recentlyViewedService: RecentlyViewedService,
        requestService: RequestService,
        searchResultFactory: SearchResultFactoryService,
        dfEventService: DfEventService,
        timeoutService: TimeoutService,
        private klevuTrackService: KlevuTrackService,
        @Inject(NDfModuleKlevuSearch.OPTIONS) private moduleConfig: NDfModuleKlevuSearch.IModuleOptions
    ) {
        super(requestService, searchResultFactory, timeoutService, dfEventService);
    }

    /**
     * Config getter
     */
    get config(): KlevuSearch.IConfig {
        return this.app.data?.klevu || ({} as KlevuSearch.IConfig);
    }

    get apiKey(): string | undefined {
        return this.config?.apiKey;
    }

    /**
     * Sends search request
     */
    protected doSearch(request: Search.IRequest): Promise<Search.IResult> {
        return new Promise<Search.IResult>((resolve, reject) => {
            this.loadRedirects().finally(() => {
                let redirect = this.getRedirect(request.q);

                if (redirect && this.url.toUrl(redirect) === location.href) {
                    redirect = undefined;
                }

                if (!request.autocomplete && redirect) {
                    resolve({
                        redirect,
                        result: {},
                        count: {}
                    } as Search.IResult);
                } else {
                    const searchRequest = this.getSearchRequest(request);
                    this.sendSearchRequest(searchRequest)
                        .then((response: KlevuSearch.ISearchResponse) => {
                            const searchResult: Search.IResult | undefined = this.getSearchResult(response);

                            if (!searchResult) {
                                throw new Error('Search result is empty');
                            }
                            searchResult.redirect = redirect;

                            resolve(searchResult);

                            this.trackSearchResult(response);
                        })
                        .catch((err: KlevuSearch.IErrorResponse) => {
                            reject((err && err.errorMessage) || STATUS_ERROR);
                        });
                }
            });
        });
    }

    /**
     * Sends search request
     */
    private getSearchRequest(request: Search.IRequest): KlevuSearch.ISearchRequest {
        const suggestions: KlevuSearch.ISuggestionRequestItem[] = [];

        // push terms requests
        if (request.terms) {
            suggestions.push({
                id: TERMS_SEARCH_ID,
                typeOfQuery: 'AUTO_SUGGESTIONS',
                query: request.q,
                limit: request.terms
            });
        }

        // record queries
        const recordQueries: KlevuSearch.ISearchRequestItem[] = [];

        [
            { klevuId: 'KLEVU_PRODUCT', qty: request.product, id: PRODUCTS_SEARCH_ID },
            { klevuId: 'KLEVU_CATEGORY', qty: request.category, id: CATEGORIES_SEARCH_ID },
            { klevuId: 'KLEVU_CMS', qty: request.page, id: CMS_SEARCH_ID }
        ].forEach(item => {
            if (item.qty) {
                recordQueries.push({
                    id: item.id,
                    typeOfRequest: KlevuSearch.ETypeOfRequest.Search,
                    settings: {
                        query: { term: request.q },
                        limit: item.qty,
                        fields: ['id'],
                        typeOfRecords: [<KlevuSearch.TRecord>item.klevuId],
                        searchPrefs: ['showOutOfStockProducts']
                    }
                });
            }
        });

        return {
            context: this.getRequestContextPart(),
            suggestions,
            recordQueries
        };
    }

    protected getRequestContextPart(): KlevuSearch.IContext {
        return {
            apiKeys: [this.config.apiKey]
        };
    }

    /**
     * Sends search request
     */
    protected sendSearchRequest(
        request: KlevuSearch.ISearchRequest | KlevuSearch.ISortRequest | KlevuSearch.IRecommendationRequest
    ): Promise<KlevuSearch.ISearchResponse> {
        let baseEndpoint = this.config.endpoint;

        // Add /cs/v2/ path if missing from endpoint
        if (baseEndpoint && !baseEndpoint.includes('/cs/v2/')) {
            baseEndpoint = `${baseEndpoint}/cs/v2`;
        }

        const endpoint = `${baseEndpoint}/search/`;

        return this.http
            .post<KlevuSearch.ISearchResponse>(endpoint, request)
            .toPromise() as Promise<KlevuSearch.ISearchResponse>;
    }

    /**
     * Transform klevu response to search result
     * @param {KlevuSearch.ISuggestionResponse} response
     * @returns {Search.IResult}
     */
    protected getSearchResult(
        response: KlevuSearch.ISearchResponse,
        matchingRules: { [type: string]: string[] } = {
            product: [PRODUCTS_SEARCH_ID, CATNAV_SEARCH_ID],
            category: [CATEGORIES_SEARCH_ID],
            page: [CMS_SEARCH_ID]
        }
    ): Search.IResult | undefined {
        try {
            const unparsed = mapValues(matchingRules, searchId => {
                const item = response?.queryResults?.find(i => searchId.includes(i.id));
                return {
                    ids: item?.records?.map(i => i.id) || [],
                    qty: item?.meta.totalResultsFound || 0
                };
            });

            return {
                count: {
                    product: unparsed['product']?.qty || 0,
                    page: unparsed['page']?.qty || 0,
                    category: unparsed['category']?.qty || 0
                },
                terms: response.suggestionResults
                    ?.find(i => i.id === TERMS_SEARCH_ID)
                    ?.suggestions.map(i => {
                        const phrase = i.suggest.toLocaleLowerCase();
                        return {
                            phrase,
                            barePhrase: phrase.replace(/<[^>]*>?/gm, '').toLocaleLowerCase()
                        };
                    }),
                result: {
                    product: this.getProductsIds((unparsed['product']?.ids as string[]) || []),
                    page: this.getPagesIds((unparsed['page']?.ids as string[]) || []),
                    category: this.getCategoryIds((unparsed['category']?.ids as string[]) || [])
                },
                direct: response
            } as Search.IResult;
        } catch (err) {
            this.app.logger.error(err);
        }
        return undefined;
    }

    protected getProductsIds(ids: string[]): number[] {
        return compact(ids.filter(id => this.isProductId(id)).map(productId => parseInt(productId, 10)));
    }

    private getPagesIds(ids: string[]): number[] {
        return compact(ids.filter(id => this.isPageId(id)).map(cmsId => this.extractId(cmsId)));
    }

    private getCategoryIds(ids: string[]): number[] {
        return compact(ids.filter(id => this.isCategoryId(id)).map(cmsId => this.extractId(cmsId)));
    }

    private extractId(id: string): number | undefined {
        const numberMatch = id.match(/\d+/g);
        return numberMatch ? parseInt(numberMatch[0], 10) : undefined;
    }

    protected isPageId(id: string): boolean {
        return id.includes('cms');
    }

    private isCategoryId(id: string): boolean {
        return id.includes('category');
    }

    protected isProductId(id: string): boolean {
        return !this.isPageId(id) && !this.isCategoryId(id);
    }

    /**
     * Loads extra klevu variables
     * I am not sure how this works, this is based on their old website
     */
    loadKlevuVars(onSuccess: IRequestCallbackFunction, onError?: IRequestCallbackFunction): void {
        const key = this.apiKey;

        if (key && typeof window !== 'undefined') {
            window.klevu_urlProtocol = location.protocol;
            window.klevu_javascriptDomain = '';

            this.requestService.loadScript(`https://js.klevu.com/klevu-js-v1/klevu-js-api/${key}.js?t=0`, {
                onLoad: () => {
                    this.webstorePopularTerms = window.klevu_webstorePopularTerms || undefined;
                    this.noResultMessage = window.klevu_uc_userOptions?.noResultsOptions?.messages[0]?.message;
                    const popularProductsOfSite = window.klevu_popularProductsOfSite;
                    const productIds = popularProductsOfSite?.map(i => parseInt(i.id, 10)).filter(id => !!id);

                    if (productIds?.length) {
                        this.app.resources.product
                            .loadAllIds(productIds, 2, { safe: true })
                            .then(products => {
                                this.popularProductsOfSite = <Product<IProductDataV2>[]>products;
                            })
                            .finally(() => onSuccess());
                    } else {
                        onSuccess();
                    }
                },
                onError
            });
        } else if (onError) {
            onError();
        }
    }

    loadKlevuVarsPromise(status?: Status, onSuccess?: IRequestCallback, onError?: IRequestCallback): Promise<any> {
        return this.requestService.send(
            status,
            () => {
                return new Promise((resolve, reject) => {
                    this.loadKlevuVars(resolve, reject);
                });
            },
            onSuccess,
            onError
        );
    }

    async loadKlevuBanners(): Promise<KlevuSearch.IBanner[]> {
        if (this.banners) {
            return this.banners;
        }
        const key = this.apiKey;
        if (!key) {
            throw new Error('Klevu API key is not set');
        }

        if (typeof window !== 'undefined') {
            await this.requestService.loadScriptAsync(`https://js.klevu.com/klevu-js-v1/klevu-js-api/${key}-banner.js`);
            this.banners = window.klevu_banner;

            return this.banners || [];
        } else {
            return [];
        }
    }

    getRedirect(query: string): string | undefined {
        if (typeof query === 'string') {
            query = query.trim().toLowerCase();
        }

        if (query && this.redirects?.length) {
            const match = find(this.redirects, redirect => redirect.keywords.includes(query));

            if (match?.url) {
                const redirect = match.url;

                if (redirect.indexOf(this.url.storeUrl) === 0) {
                    return redirect.substr(this.url.storeUrl.length);
                }

                return redirect;
            }
        }

        return undefined;
    }

    get redirects(): KlevuSearch.IKeywordUrlMap[] | undefined {
        if (!this._redirects && typeof window !== 'undefined' && window.klevu_keywordUrlMap) {
            this._redirects = window.klevu_keywordUrlMap.map(keyword => {
                return {
                    url: keyword.url,
                    keywords: compact(
                        flatten(
                            (keyword.keywords || []).map(keywordItem => keywordItem.split('\r\n').map(word => word.trim().toLowerCase()))
                        )
                    )
                };
            });
        }

        return this._redirects;
    }

    async loadRedirects(): Promise<KlevuSearch.IKeywordUrlMap[]> {
        const url = this.getRedirectsMapUrl();
        if (!url) {
            throw new Error('Klevu API key is not set');
        }
        await this.requestService.loadScript(url);
        return this.redirects || [];
    }

    private getRedirectsMapUrl(): string | undefined {
        if (this.apiKey) {
            return `https://js.klevu.com/klevu-js-v1/klevu-js-api/${this.apiKey}-maps.js`;
        }

        return undefined;
    }

    /**
     * Retrieves sorted list of products
     */
    sendCategoryOrder(category: Category<ICategoryDataV3>): Promise<number[]> {
        const categoryPath = this.getCategoryPath(category);
        const request: KlevuSearch.ISortRequest = {
            context: this.getRequestContextPart(),
            recordQueries: [
                {
                    id: CATNAV_SEARCH_ID,
                    typeOfRequest: 'CATNAV',
                    settings: {
                        limit: this.moduleConfig.limit,
                        fields: ['id'],
                        query: {
                            categoryPath: this.getCategoryPath(category)
                        },
                        ...this.klevuTrackService.getAbParams(KlevuSearch.catNavSearchType, categoryPath)
                    }
                }
            ]
        };

        return new Promise((resolve, reject) => {
            this.sendSearchRequest(request)
                .then(response => {
                    resolve(this.getSearchResult(response)?.result?.product || []);
                })
                .catch(reject);
        });
    }

    getEligibleBanners(term?: string): KlevuSearch.IBanner[] {
        if (this.banners) {
            const getDate = (dateString?: string, start = true): Date | undefined => {
                const parts = dateString?.split('/') || [];

                if (parts.length === 3) {
                    const year = parseInt(parts[0]);
                    const month = parseInt(parts[1]);
                    const day = parseInt(parts[2]);

                    if (![year, month, day].find(i => typeof i !== 'number')) {
                        const hour = start ? 0 : 23;
                        const minute = start ? 0 : 59;
                        const second = start ? 0 : 59;

                        return ukDate(year, month - 1, day, hour, minute, second);
                    }
                }

                return undefined;
            };

            const now = new Date();

            const banners = this.banners.filter(banner => {
                const startDate = banner.startDate && getDate(banner.startDate);
                const endDate = banner.endDate && getDate(banner.endDate, false);

                return (!startDate || startDate <= now) && (!endDate || endDate >= now);
            });

            const lowerCaseTerm = term?.toLocaleLowerCase();

            return term ? banners.filter(banner => banner.showForTerms?.some(i => i.toLocaleLowerCase() === lowerCaseTerm)) : banners;
        }

        return [];
    }

    /**
     * Retrieves category path for sort order request
     */
    getCategoryPath(category: Category<ICategoryDataV3>): string {
        return category?.data?.breadcrumbs?.map(i => i.label).join(';') || '';
    }

    ////////////////////////////////////////////
    ////////
    ////////    Recommendations
    ////////
    ////////////////////////////////////////////

    loadRecommendationsProducts(options: KlevuSearch.IRecommendationsOptions): Promise<Product[]> {
        return new Promise((resolve, reject) => {
            this.loadRecommendationsProductsIds(options)
                .then(ids => {
                    if (ids?.length) {
                        this.app.resources.product
                            .loadAllIds(ids, 2, { safe: true })
                            .then(() => {
                                resolve(this.app.resources.product.getAllExisting(ids));
                            })
                            .catch(reject);
                    } else {
                        resolve([]);
                    }
                })
                .catch(reject);
        });
    }

    loadRecommendationsProductsIds(options: KlevuSearch.IRecommendationsOptions): Promise<number[]> {
        return new Promise((resolve, reject) => {
            this.loadRecommendations(options)
                .then(response => {
                    resolve(this.getProductsIds((response?.queryResults?.[0]?.records?.map(i => i.id).filter(i => !!i) as string[]) || []));
                })
                .catch(reject);
        });
    }

    private loadRecommendations(options: KlevuSearch.IRecommendationsOptions): Promise<KlevuSearch.ISearchResponse> {
        return new Promise((resolve, reject) => {
            this.getRecommendationRequest(options)
                .then(request => {
                    this.sendSearchRequest(request).then(resolve).catch(reject);
                })
                .catch(reject);
        });
    }

    private getRecommendationRequest(options: KlevuSearch.IRecommendationsOptions): Promise<KlevuSearch.IRecommendationRequest> {
        return new Promise((resolve, reject) => {
            const typeId = options.klevuTypeId;
            if (typeId) {
                if (
                    (options.useRecommendationId || KlevuSearch.ERecommendationTypeId.HandPicked === typeId) &&
                    options.klevuRecommendationId
                ) {
                    this.klevuTrackService
                        .loadRecommendationsConfig(options.klevuRecommendationId)
                        .then(configuration => {
                            const payload = configuration?.search?.payload;
                            if (payload) {
                                try {
                                    const request: KlevuSearch.IRecommendationRequest = {
                                        context: this.getRequestContextPart(),
                                        ...JSON.parse(payload)
                                    };
                                    resolve(request);
                                } catch (e) {
                                    reject();
                                }
                            } else {
                                reject();
                            }
                        })
                        .catch(reject);
                } else {
                    this.getRecommendationRequestSettings(options)
                        .then(settings => {
                            resolve({
                                context: this.getRequestContextPart(),
                                recordQueries: [
                                    {
                                        id: typeId,
                                        typeOfRequest: this.getRecommendationTypeOfRequest(typeId),
                                        settings
                                    }
                                ]
                            });
                        })
                        .catch(reject);
                }
            } else {
                reject();
            }
        });
    }

    private getRecommendationRequestSettings(
        options: KlevuSearch.IRecommendationsOptions
    ): Promise<KlevuSearch.IRecommendationRequestSettings> {
        return new Promise((resolve, reject) => {
            const promises: (Promise<any> | undefined)[] = [];

            const typeId = options.klevuTypeId;

            const productsIds = options.klevuProductsIds || [];
            const categoryId = options.klevuCategoryId;

            if (
                categoryId &&
                [KlevuSearch.ERecommendationTypeId.NewArrivals, KlevuSearch.ERecommendationTypeId.TrendingProducts].indexOf(typeId) > -1
            ) {
                promises.push(this.app.resources.category.load(categoryId, 3, { safe: true }));
            }

            if (
                productsIds?.length &&
                [
                    KlevuSearch.ERecommendationTypeId.SimilarProducts,
                    KlevuSearch.ERecommendationTypeId.TrendingProducts,
                    KlevuSearch.ERecommendationTypeId.CustomersAlsoViewed,
                    KlevuSearch.ERecommendationTypeId.CustomersAlsoBought,
                    KlevuSearch.ERecommendationTypeId.HandPicked,
                    KlevuSearch.ERecommendationTypeId.VisuallySimilar
                ].indexOf(typeId) > -1
            ) {
                promises.push(this.app.resources.product.loadAllIds(productsIds, 3, { safe: true }));
            }

            if (KlevuSearch.ERecommendationTypeId.RecentlyViewed === typeId) {
                promises.push(this.recentlyViewedService.load(3));
            }

            const finish = () => {
                const products = this.app.resources.product.getAllExisting(productsIds) as Product<IProductDataV3>[];
                const klevuProductsIds = products.map(i => this.getKlevuProductId(i));
                const category = this.app.resources.category.getExisting(categoryId) as Category<ICategoryDataV3>;
                const categoryPath = this.getCategoryPath(category);

                const settings: KlevuSearch.IRecommendationSettings = {
                    typeOfRecords: [KlevuSearch.ETypeOfRecord.Product]
                };

                const recentObjectsContext: KlevuSearch.IRecentObjectContext = {
                    recentObjects: [
                        {
                            typeOfRecord: KlevuSearch.ETypeOfRecord.Product,
                            records: klevuProductsIds.map(i => {
                                return {
                                    id: i
                                };
                            })
                        }
                    ]
                };

                switch (typeId) {
                    case KlevuSearch.ERecommendationTypeId.NewArrivals:
                        resolve({ ...settings, query: { categoryPath } });
                        break;
                    case KlevuSearch.ERecommendationTypeId.SimilarProducts:
                    case KlevuSearch.ERecommendationTypeId.CustomersAlsoViewed:
                    case KlevuSearch.ERecommendationTypeId.CustomersAlsoBought:
                        resolve({ ...settings, context: recentObjectsContext });
                        break;
                    case KlevuSearch.ERecommendationTypeId.TrendingProducts:
                        resolve({
                            ...settings,
                            query: { categoryPath },
                            context: recentObjectsContext,
                            personalisation: { enablePersonalisation: options.klevuEnablePersonalisation }
                        });
                        break;
                    case KlevuSearch.ERecommendationTypeId.RecentlyViewed:
                        resolve(
                            this.getRecommendationCustomAndQuerySettings(
                                this.recentlyViewedService.getItems().map(i => this.getKlevuProductId(i as Product<IProductDataV3>))
                            )
                        );
                        break;
                    case KlevuSearch.ERecommendationTypeId.VisuallySimilar:
                        resolve({
                            ...settings,
                            limit: options.klevuLimit,
                            context: {
                                sourceObjects: [
                                    {
                                        typeOfRecord: KlevuSearch.ETypeOfRecord.Product,
                                        records: klevuProductsIds.map(i => {
                                            return {
                                                id: i
                                            };
                                        })
                                    }
                                ]
                            }
                        });
                        break;
                    case KlevuSearch.ERecommendationTypeId.HandPicked:
                        resolve(this.getRecommendationCustomAndQuerySettings(klevuProductsIds));
                        break;
                }
            };

            const actualPromises = promises.filter(i => !!i);
            if (actualPromises.length) {
                Promise.all(actualPromises).finally(finish);
            } else {
                finish();
            }
        });
    }

    private getRecommendationTypeOfRequest(typeId: KlevuSearch.ERecommendationTypeId): KlevuSearch.ETypeOfRequest {
        switch (typeId) {
            case KlevuSearch.ERecommendationTypeId.NewArrivals:
                return KlevuSearch.ETypeOfRequest.NewArrivals;
            case KlevuSearch.ERecommendationTypeId.SimilarProducts:
                return KlevuSearch.ETypeOfRequest.Similar;
            case KlevuSearch.ERecommendationTypeId.TrendingProducts:
                return KlevuSearch.ETypeOfRequest.Trending;
            case KlevuSearch.ERecommendationTypeId.CustomersAlsoViewed:
                return KlevuSearch.ETypeOfRequest.AlsoViewed;
            case KlevuSearch.ERecommendationTypeId.CustomersAlsoBought:
                return KlevuSearch.ETypeOfRequest.AlsoBought;
            case KlevuSearch.ERecommendationTypeId.RecentlyViewed:
            case KlevuSearch.ERecommendationTypeId.HandPicked:
                return KlevuSearch.ETypeOfRequest.Search;
            case KlevuSearch.ERecommendationTypeId.VisuallySimilar:
                return KlevuSearch.ETypeOfRequest.VisuallySimilar;
        }
    }

    getKlevuProductId(product: Product<IProductDataV3>): string {
        return this.klevuTrackService.getProductId(product);
    }

    /**
     * Returns klevu trending products promise, to show for example  within quick search before term is entered.
     */
    getKlevuTrendingProductsPromise(request?: Search.IRequest): Promise<SearchResult> {
        !this.trendingProductsPromise &&
            (this.trendingProductsPromise = this.search({
                ...{ q: '*', autocomplete: false, redirect: false, product: 10 },
                ...(request || {})
            }));
        return this.trendingProductsPromise;
    }

    private getRecommendationCustomAndQuerySettings(klevuProductsIds: string[]): KlevuSearch.IRecommendationCustomAndQuerySettings {
        return {
            customANDQuery: `id:(${klevuProductsIds.map(i => `\"${i}\"`)})`,
            topIds: klevuProductsIds.map(i => {
                return {
                    key: 'id',
                    value: i
                };
            }),
            typeOfRecords: [KlevuSearch.ETypeOfRecord.Product]
        };
    }

    private async loadContextRecommendations(
        id: string,
        query: { pid: string; gpid?: number } | { cp: string },
        isRecentlyViewed?: boolean
    ): Promise<number[]> {
        const config = await this.klevuTrackService.loadRecommendationsConfig(id, query);
        isRecentlyViewed && (await this.recentlyViewedService.load(3));
        const productIds = compact([(<any>query).pid, (<any>query).gpid]);
        const request = {
            context: this.getRequestContextPart(),
            ...this.getContextRecommendationsPayload(config.search.payload, productIds, isRecentlyViewed)
        } as KlevuSearch.IRecommendationRequest;

        const response = await this.sendSearchRequest(request);

        return (
            this.getSearchResult(response, { product: request.recordQueries?.map(i => i.id).filter(i => !!i) as string[] })?.result
                ?.product || []
        );
    }

    async loadContextRecommendationsForProduct(
        id: string,
        klevuProductsId: string,
        parentId?: number,
        isRecentlyViewed?: boolean
    ): Promise<number[]> {
        return this.loadContextRecommendations(id, { pid: klevuProductsId, gpid: parentId }, isRecentlyViewed);
    }

    async loadContextRecommendationsForCategory(id: string, categoryPath: string, isRecentlyViewed?: boolean): Promise<number[]> {
        return this.loadContextRecommendations(id, { cp: categoryPath }, isRecentlyViewed);
    }

    async loadContextRecommendationsForBasket(id: string, productsIds: string[], isRecentlyViewed?: boolean): Promise<number[]> {
        const config = await this.klevuTrackService.loadRecommendationsConfig(id);
        isRecentlyViewed && (await this.recentlyViewedService.load(3));
        const payload = this.getContextRecommendationsPayload(config.search.payload, productsIds, isRecentlyViewed);

        const request = {
            context: this.getRequestContextPart(),
            ...payload
        } as KlevuSearch.IRecommendationRequest;
        const response = await this.sendSearchRequest(request);
        return (
            this.getSearchResult(response, { product: request.recordQueries?.map(i => i.id).filter(i => !!i) as string[] })?.result
                ?.product || []
        );
    }

    private getContextRecommendationsPayload(payloadString: string, productsIds: string[], isRecentlyViewed?: boolean) {
        const payload = JSON.parse(payloadString);

        if (productsIds?.length && payload.recordQueries) {
            payload.recordQueries[0].settings.context = {
                recentObjects: [
                    {
                        typeOfRecord: 'KLEVU_PRODUCT',
                        records: productsIds.map(id => ({ id: id }))
                    }
                ]
            };
        }
        if (payload.recordQueries?.[0]?.id === 'klevuRECSItemList' && isRecentlyViewed) {
            payload.recordQueries[0].settings = this.getRecommendationCustomAndQuerySettings(
                this.recentlyViewedService.getItems().map(i => this.getKlevuProductId(i as Product<IProductDataV3>))
            );
        }

        return payload;
    }

    get noSearchResultsMessages(): KlevuSearch.INoResultsMessage[] | undefined {
        return (this.window as Window).klevu_uc_userOptions?.noResultsOptions?.messages;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Tracking
    ////////
    ////////////////////////////////////////////

    /**
     * Search result tracking
     */
    protected trackSearchResult(response: KlevuSearch.ISearchResponse) {
        this.klevuTrackService.trackSearchResult(response);
    }

    /**
     * Sends tracking request for product click
     * @param term search term
     * @param product Clicked product
     */
    trackClick(term: string, product: Product<IProductDataV2>) {
        this.klevuTrackService.trackSearchResultProductClick(term, product);
    }
}
